import * as React from 'react';
import { useCallback } from 'react';
import Box from '@mui/material/Box';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { Button } from '@mui/material';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { TreeItemContainer } from './styles';

interface Props {
  onAddItem: Function;
  onEditItem: Function;
  data: any;
  setData: Function;
}

const EBTreeView: React.FC<Props> = ({
  onAddItem,
  data,
  onEditItem,
  setData,
}) => {
  const insertTreeItem = useCallback(
    (parent: any, draggedItem: any, targetItem: any) => {
      const newItems = parent.children.map((node: any) => {
        const newNode = { ...node };

        const hasWidgetId = node.widgetId === targetItem.widgetId;
        const hasntWidgetId = node.tempId === targetItem.tempId;

        const toAdd = node.widgetId ? hasWidgetId : hasntWidgetId;

        if (toAdd) {
          if (node.children) {
            newNode.children.push({
              ...draggedItem,
              parentId: node.widgetId ? node.widgetId : node.tempId,
            });
          } else {
            newNode.children = [
              {
                ...draggedItem,
                parentId: node.widgetId ? node.widgetId : node.tempId,
              },
            ];
          }
        } else if (node.children && node.children.length > 0) {
          return insertTreeItem(node, draggedItem, targetItem);
        }

        return newNode;
      });

      if (parent.widgetId === targetItem.widgetId) {
        newItems.push(draggedItem);
      }

      return { ...parent, children: newItems };
    },
    [],
  );

  const removeTreeItem = useCallback((parent: any, itemToRemove: any) => {
    const newItems = parent.children.map((node: any) => {
      if (node.children) {
        return removeTreeItem(node, itemToRemove);
      }

      return node;
    });

    if (!itemToRemove.widgetId) {
      const filteredItems = newItems.filter(
        (item: any) => item.tempId !== itemToRemove.tempId,
      );

      return { ...parent, children: filteredItems };
    }

    const filteredItems = newItems.filter(
      (item: any) => item.widgetId !== itemToRemove.widgetId,
    );

    return { ...parent, children: filteredItems };
  }, []);

  const renderLabel = useCallback(
    (node: any): any => (
      <TreeItemContainer>
        <span>{node.name}</span>
        <div>
          {node.name !== 'widgets list' && (
            <Button
              variant="text"
              size="small"
              style={{ minWidth: 'unset' }}
              onClick={e => {
                e.stopPropagation();
                onEditItem(node, false);
              }}
            >
              <ModeEditIcon style={{ fontSize: '0.8vw' }} />
            </Button>
          )}

          <Button
            variant="text"
            size="small"
            style={{ minWidth: 'unset' }}
            onClick={e => {
              e.stopPropagation();
              onAddItem(node, true);
            }}
          >
            <AddCircleIcon style={{ fontSize: '0.8vw' }} />
          </Button>
        </div>
      </TreeItemContainer>
    ),
    [onAddItem, onEditItem],
  );

  const ItemTree = useCallback(
    (props: any): any => {
      const { children, node, ...other } = props;

      return (
        <TreeItem
          draggable
          onDragStart={(event: any) => {
            event.dataTransfer.setData(
              'application/json',
              JSON.stringify(node),
            );
            event.stopPropagation();
          }}
          onDrop={(event: any) => {
            const draggedItem = JSON.parse(
              event.dataTransfer.getData('application/json'),
            );

            if (
              draggedItem.widgetId !== node.widgetId ||
              draggedItem.tempId !== node.tempId
            ) {
              const objData = {
                name: 'widgets list',
                widgetId: data.widgetId,
                children: data.children,
              };

              // the removeItem() must to be first then insertItem()
              const dataWithoutItem = removeTreeItem(objData, draggedItem);
              const dataWithNewItem = insertTreeItem(
                dataWithoutItem,
                draggedItem,
                node,
              );

              setData(dataWithNewItem);
            }

            event.stopPropagation();
          }}
          onDragOver={(event: any) => {
            event.preventDefault();
          }}
          label={renderLabel(node)}
          {...other}
        >
          {children}
        </TreeItem>
      );
    },
    [
      data.children,
      data.widgetId,
      insertTreeItem,
      removeTreeItem,
      renderLabel,
      setData,
    ],
  );

  const buildTree = useCallback(
    (obj: any, key: string) => {
      return (
        <ItemTree node={obj} key={key} itemId={key}>
          {obj.children?.map((node: any, index: number) => {
            const newKey = `${key}-${index}`;
            return buildTree(node, newKey);
          })}
        </ItemTree>
      );
    },
    [ItemTree],
  );

  return (
    <Box id="box" sx={{ minWidth: '14vw' }}>
      <SimpleTreeView
        id="simpleTree"
        style={{ display: 'flex', margin: '1.3vw' }}
        defaultExpandedItems={['0']}
      >
        {buildTree(
          {
            name: 'widgets list',
            widgetId: data.widgetId,
            children: data.children,
          },
          '0',
        )}
      </SimpleTreeView>
    </Box>
  );
};

export default EBTreeView;
